/**
 * @fileoverview Funciones de detección de patrones de coordenadas PTEL
 * @module lib/coordinate/detectors
 * 
 * Extraído de coordinateNormalizer.ts como parte de TD-002.B
 * Contiene funciones para detectar placeholders, códigos, pares de coordenadas
 * y clasificar patrones de formato.
 * 
 * @version 1.0.0
 * @date 2026-01-25
 */

import type { 
  PatronDetectado, 
  ResultadoPrefiltroCodigo,
  ResultadoParEspaciosMultiples 
} from './types';

import { 
  RANGOS_ANDALUCIA, 
  PLACEHOLDERS_TEXTO, 
  PLACEHOLDERS_NUMERICOS 
} from './constants';

// ============================================================================
// FUNCIONES DE DETECCIÓN DE PATRONES
// ============================================================================

/**
 * Detecta si el valor es un placeholder (texto o numérico)
 * 
 * @param valor - Valor a evaluar
 * @returns true si es un placeholder conocido
 * 
 * @example
 * esPlaceholder('indicar')  // true
 * esPlaceholder('pendiente') // true
 * esPlaceholder('504750')    // false
 * esPlaceholder('0')         // true (placeholder numérico)
 */
export function esPlaceholder(valor: string): boolean {
  // Coerción defensiva a string
  const valorStr = valor != null ? String(valor) : '';
  const valorLimpio = valorStr.trim().toLowerCase();
  
  // Placeholder texto
  if (PLACEHOLDERS_TEXTO.has(valorLimpio)) {
    return true;
  }
  
  // Vacío
  if (valorLimpio === '' || valorLimpio === 'null' || valorLimpio === 'undefined') {
    return true;
  }
  
  // Placeholder numérico
  const numero = parseFloat(valorLimpio);
  if (!isNaN(numero) && PLACEHOLDERS_NUMERICOS.has(numero)) {
    return true;
  }
  
  return false;
}

/**
 * PRE-FILTRO: Detecta si un valor es un código/ID, no una coordenada.
 * 
 * Basado en análisis empírico de 22 documentos PTEL:
 * - ID_14_DIGITOS: 9 casos (ej: "63857927161771")
 * - CODIGO_CERO_INICIAL: 4 casos (ej: "06932587")
 * - CODIGO_8_DIGITOS_ALTO: 7 casos (ej: "76039710")
 * 
 * @param valor - Valor a analizar
 * @returns Resultado con indicación de si es código y la razón
 * 
 * @example
 * esCodigo('63857927161771')  // { esCodigo: true, patron: 'ID_14_DIGITOS' }
 * esCodigo('06932587')        // { esCodigo: true, patron: 'CERO_INICIAL_LARGO' }
 * esCodigo('504750')          // { esCodigo: false, patron: null }
 */
export function esCodigo(valor: string): ResultadoPrefiltroCodigo {
  const resultado: ResultadoPrefiltroCodigo = {
    esCodigo: false,
    razon: null,
    patron: null,
  };
  
  if (!valor || typeof valor !== 'string') return resultado;
  
  const v = valor.trim();
  
  // Extraer solo dígitos para análisis
  const soloDigitos = v.replace(/[^\d]/g, '');
  
  // Ignorar si tiene separadores decimales válidos (coma o punto seguido de decimales)
  const tieneDecimal = /^\d+[,\.]\d+$/.test(v) || /^\d{1,3}(?:\.\d{3})+,\d+$/.test(v);
  
  // Patrón 1: Exactamente 14 dígitos sin separadores → ID
  if (soloDigitos.length === 14 && /^\d{14}$/.test(v)) {
    resultado.esCodigo = true;
    resultado.razon = `ID de 14 dígitos detectado: "${v}"`;
    resultado.patron = 'ID_14_DIGITOS';
    return resultado;
  }
  
  // Patrón 2: Empieza con 0 y tiene más de 6 dígitos → código con ceros de relleno
  if (/^0\d{6,}$/.test(v) && !tieneDecimal) {
    resultado.esCodigo = true;
    resultado.razon = `Código con cero inicial detectado: "${v}"`;
    resultado.patron = 'CERO_INICIAL_LARGO';
    return resultado;
  }
  
  // Patrón 3: Más de 12 dígitos sin separador decimal → demasiado largo para UTM
  // EXCEPCIÓN BE-01: 13-14 dígitos empezando por '4' pueden ser PEGADAS (Y+X fusionadas)
  const puedeSerPegadas = (soloDigitos.length === 13 || soloDigitos.length === 14) && 
                          soloDigitos.startsWith('4');
  // EXCEPCIÓN BE-01b: 12-14 dígitos NO empezando por '4' pueden ser CONCATENADO (X+Y fusionadas)
  // Ejemplo: "5047504077905" → X=504750, Y=4077905
  const puedeSerConcatenado = (soloDigitos.length >= 12 && soloDigitos.length <= 14) && 
                               !soloDigitos.startsWith('4');
  if (soloDigitos.length > 12 && !tieneDecimal && /^\d+$/.test(v) && !puedeSerPegadas && !puedeSerConcatenado) {
    resultado.esCodigo = true;
    resultado.razon = `Valor con ${soloDigitos.length} dígitos, demasiado largo para coordenada UTM: "${v}"`;
    resultado.patron = 'DEMASIADOS_DIGITOS';
    return resultado;
  }
  
  return resultado;
}

/**
 * Detecta pares X/Y concatenados en una celda separados por espacios múltiples.
 * 
 * Basado en análisis empírico de 22 documentos PTEL:
 * - 8 casos detectados, 1 recuperable: "439501           4142536"
 * 
 * @param valor - Valor a analizar
 * @returns Resultado con X, Y extraídas y validación de rangos
 * 
 * @example
 * detectarParEspaciosMultiples('439501           4142536')
 * // { esPar: true, valorX: 439501, valorY: 4142536, valido: true }
 */
export function detectarParEspaciosMultiples(valor: string): ResultadoParEspaciosMultiples {
  const resultado: ResultadoParEspaciosMultiples = {
    esPar: false,
    valorX: null,
    valorY: null,
    valido: false,
    warning: null,
  };
  
  if (!valor || typeof valor !== 'string') return resultado;
  
  const v = valor.trim();
  
  // Patrón: 5-6 dígitos + 2 o más espacios + 7 dígitos
  // X UTM Andalucía: 100000-620000 (5-6 dígitos)
  // Y UTM Andalucía: 3980000-4290000 (7 dígitos)
  const match = v.match(/^(\d{5,6})\s{2,}(\d{7})$/);
  
  if (!match) return resultado;
  
  const [, xStr, yStr] = match;
  const x = parseInt(xStr, 10);
  const y = parseInt(yStr, 10);
  
  resultado.esPar = true;
  resultado.valorX = x;
  resultado.valorY = y;
  
  // Validar rangos UTM Andalucía
  const xValido = x >= RANGOS_ANDALUCIA.UTM.X_MIN && x <= RANGOS_ANDALUCIA.UTM.X_MAX;
  const yValido = y >= RANGOS_ANDALUCIA.UTM.Y_MIN && y <= RANGOS_ANDALUCIA.UTM.Y_MAX;
  
  if (xValido && yValido) {
    resultado.valido = true;
    resultado.warning = `Par X/Y detectado en una celda: X=${x}, Y=${y}`;
  } else {
    resultado.warning = `Par detectado pero fuera de rango UTM Andalucía: X=${x} (${xValido ? 'OK' : 'INVÁLIDO'}), Y=${y} (${yValido ? 'OK' : 'INVÁLIDO'})`;
  }
  
  return resultado;
}

/**
 * Detecta el patrón de formato de la coordenada
 * 
 * Clasificador principal que identifica 30+ patrones de formato de coordenadas
 * encontrados en documentos PTEL andaluces.
 * 
 * @param valor - Valor de coordenada a clasificar
 * @returns Tipo de patrón detectado
 * 
 * @example
 * detectarPatron('504750,88')   // 'COMA_DECIMAL'
 * detectarPatron('4.077.905,68') // 'EUROPEO_COMPLETO'
 * detectarPatron('POINT(504750 4077905)') // 'WKT_POINT'
 * detectarPatron('37°26\'46.5"N') // 'DMS_ESTANDAR'
 */
export function detectarPatron(valor: string): PatronDetectado {
  // Coerción defensiva a string
  const valorStr = valor != null ? String(valor) : '';
  const v = valorStr.trim();
  
  if (esPlaceholder(v)) {
    return 'PLACEHOLDER';
  }
  
  // G2: WKT POINT format
  if (/^\s*POINT\s*\(/i.test(v)) {
    return 'WKT_POINT';
  }
  
  // G3: GeoJSON Point format
  if (v.startsWith('{') && v.includes('"type"') && v.includes('"Point"')) {
    return 'GEOJSON_POINT';
  }
  
  // C1-C8: Formatos DMS (Grados Sexagesimales)
  // Detectar antes de otros patrones porque contienen símbolos especiales
  
  // C7: Signo explícito: +37°26'46.5" o -3°45'12.3"
  if (/^[+-]\d+[°º]/.test(v)) {
    return 'DMS_SIGNO';
  }
  
  // C4: Prefijo cardinal: N37°26'46.5" o W3°45'12.3"
  if (/^[NSEWO]\s*\d+[°º]/i.test(v)) {
    return 'DMS_PREFIJO';
  }
  
  // C8: Dos puntos: 37:26:46.5 N o 37:26:46.5N
  if (/^\d+:\d+:\d+\.?\d*\s*[NSEWO]?$/i.test(v)) {
    return 'DMS_DOS_PUNTOS';
  }
  
  // C5: Minutos decimales: 37°26.775'N (sin segundos)
  if (/^\d+\s*[°º]\s*\d+\.?\d*\s*['′]\s*[NSEWO]?$/i.test(v)) {
    return 'DMS_MINUTOS_DEC';
  }
  
  // C1/C6: Estándar ISO: 37°26'46.5"N o 37° 26' 46.5" N
  if (/^\d+\s*[°º]\s*\d+\s*['′]\s*\d+\.?\d*\s*["″]?\s*[NSEWO]?$/i.test(v)) {
    // C6 tiene más espacios
    const espacios = (v.match(/\s/g) || []).length;
    return espacios > 2 ? 'DMS_ESPACIOS_EXTRA' : 'DMS_ESTANDAR';
  }
  
  // C3: Espacios como separadores: 37 26 46.5 N
  if (/^\d+\s+\d+\s+\d+\.?\d*\s*[NSEWO]$/i.test(v)) {
    return 'DMS_ESPACIOS';
  }
  
  // D1-D4: Formatos NMEA GPS
  // D3: Sentencia NMEA completa ($GPGGA, $GPRMC, $GPGLL)
  if (/^\$G[PN](GGA|RMC|GLL)/i.test(v)) {
    return 'NMEA_SENTENCIA';
  }
  
  // D1: NMEA estándar con decimales: 3726.775N o 00345.204W
  if (/^\d{4,5}\.\d+\s*,?\s*[NSEWO]$/i.test(v)) {
    return 'NMEA_ESTANDAR';
  }
  
  // D2: NMEA entero sin decimales: 3726N
  if (/^\d{4,5}\s*[NSEWO]$/i.test(v)) {
    return 'NMEA_ENTERO';
  }
  
  // D4: Par NMEA: 3726.775N, 00345.204W o similar
  if (/\d{4,5}\.?\d*\s*,?\s*[NS]\s*[,\/\s]+\s*\d{4,5}\.?\d*\s*,?\s*[EWO]/i.test(v)) {
    return 'NMEA_PAR';
  }
  
  // Detectar coordenadas concatenadas (12+ dígitos sin separadores)
  const soloDigitos = v.replace(/[^\d]/g, '');
  
  // BE-01: PEGADAS - Coordenadas Y+X fusionadas en 13-14 dígitos
  // Formato Andalucía: 4XXXXXXYYYYYY (Y empieza por 4)
  if ((soloDigitos.length === 13 || soloDigitos.length === 14) && /^\d+$/.test(v)) {
    // Verificar si empieza por 4 (característico de Y UTM Andalucía)
    if (soloDigitos.startsWith('4')) {
      return 'PEGADAS';
    }
  }
  
  if (soloDigitos.length >= 12 && /^\d+$/.test(v)) {
    return 'CONCATENADO';
  }
  
  // P2-1: Mojibake (Â´, Âº)
  if (/Â[´º]/.test(v)) {
    return 'MOJIBAKE';
  }
  
  // P1-1: Espacio + Doble tilde: "504 750´´92"
  if (/^\d{1,3}\s+\d{3}´´\d{1,2}$/.test(v) || /^\d\s+\d{3}\s+\d{3}´´\d{1,2}$/.test(v)) {
    return 'ESPACIO_DOBLE_TILDE';
  }
  
  // P1-3: Espacio + decimales implícitos: "506 527 28"
  if (/^\d{3}\s+\d{3}\s+\d{1,2}$/.test(v) || /^\d\s+\d{3}\s+\d{3}\s+\d{1,2}$/.test(v)) {
    return 'ESPACIO_DECIMAL_IMPLICITO';
  }
  
  // P1-2: Espacio separador sin decimal: "504 489" o "4 076 367"
  if (/^\d{1,3}(\s+\d{3})+$/.test(v)) {
    return 'ESPACIO_SIN_DECIMAL';
  }
  
  // P1-4: Tilde simple como decimal: "503693´77"
  if (/^\d+´\d+$/.test(v)) {
    return 'TILDE_SIMPLE';
  }
  
  // P2-3/P2-4: Comillas tipográficas o apóstrofe
  if (/['']/.test(v) && /\d+['\']d+/.test(v)) {
    return 'COMILLAS_TIPOGRAFICAS';
  }
  
  // P1-5: Formato europeo completo: "4.077.905,68"
  if (/^\d{1,3}(?:\.\d{3})+,\d+$/.test(v)) {
    return 'EUROPEO_COMPLETO';
  }
  
  // BE-01: COMA_ESPACIO_DECIMAL: "436780, 0" (Colomera)
  // Coma seguida de espacio(s) y dígitos - formato corrupto
  if (/^\d+,\s+\d+$/.test(v)) {
    return 'COMA_ESPACIO_DECIMAL';
  }
  
  // P1-6: Solo coma decimal: "436780,0"
  if (/^\d+,\d+$/.test(v)) {
    return 'COMA_DECIMAL';
  }
  
  // P1-7: Solo punto miles sin decimal: "4.230.105"
  if (/^\d{1,3}(?:\.\d{3})+$/.test(v)) {
    return 'PUNTO_MILES';
  }
  
  // Formato limpio: "521581.88" o "504750"
  if (/^-?\d+\.?\d*$/.test(v)) {
    return 'LIMPIO';
  }
  
  return 'DESCONOCIDO';
}
